import classNames from "classnames";
import React, {
  ComponentProps,
  createContext,
  FC,
  ReactNode,
  useContext,
  useState,
} from "react";
import { LuArrowLeft, LuDownload, LuLock, LuSparkles } from "react-icons/lu";
import { Link } from "react-router-dom";

import { ListPager } from "../../../components/pagination/ListPager";
import { getInitialsFromFullName } from "../../../constants/constants";
import { AiReportData } from "../../../slices/AiSlice";
import { useAiStatus } from "../../ai/useAiStatus";
import { focusRows, ReportFocus } from "../ask/reportAsk.utils";
import { ReportAskPanel } from "../ask/ReportAskPanel";
import { STATUS_COLORS, StatusGroup } from "../reports.utils";
import { useSavedViewControls } from "../saved/SavedReportControls";

export const TH =
  "whitespace-nowrap border-b border-[#EDEFF3] px-3 py-2.5 text-left text-[11.5px] font-bold uppercase tracking-[0.6px] text-[#5B6472]";
export const TD =
  "whitespace-nowrap border-b border-[#EDEFF3] px-3 py-3 text-[13.5px] text-[#1F2433]";
export const NUM = "text-right";
export const LINK =
  "font-bold text-[#1F2433] no-underline hover:text-[#6B4EFF] hover:underline";

type ReportPageProps = {
  title: string;
  description: string;
  filters?: ReactNode;
  onExport?: () => void;
  /** What the report shows, for "Ask about this report" (AI reporting, 2026-10-25). */
  ask?: AiReportData;
  askSuggestions?: string[];
  /** Extra buttons next to Export (AI-created reports). */
  actions?: ReactNode;
  /** Replaces the "All reports" back link and title row (AI-created reports). */
  header?: ReactNode;
  children: ReactNode;
};

/** "Show only these rows" from an Ask about this report answer. */
const ReportFocusContext = createContext<ReportFocus>(null);

/** The rows of `table` the person narrowed the report to (all rows otherwise). */
export const useFocusRows = <T,>(
  table: string,
  rows: T[],
  keyOf: (row: T, index: number) => string,
) => focusRows(useContext(ReportFocusContext), table, rows, keyOf);

/** Header (back link, title, Export) + filters + content of one report. */
export const ReportPage: FC<ReportPageProps> = ({
  title,
  description,
  filters,
  onExport,
  ask,
  askSuggestions,
  actions,
  header,
  children,
}) => {
  const { isOn: aiOn } = useAiStatus();
  const [askOpen, setAskOpen] = useState(false);
  const [focus, setFocus] = useState<ReportFocus>(null);
  const canAsk = aiOn && !!ask;
  // "Save as my report" / a saved view's header and menu (standard reports only).
  const saved = useSavedViewControls(description);
  return (
    <div
      className={
        "min-h-full bg-[#F6F7FB] px-4 pb-14 pt-7 font-Manrope text-[#1F2433] sm:px-8"
      }>
      <div
        className={classNames(
          "mx-auto",
          canAsk && askOpen
            ? "grid max-w-[1680px] items-start gap-[18px] lg:grid-cols-[minmax(0,1fr)_420px]"
            : "max-w-[1240px]",
        )}>
        <div className={"flex min-w-0 flex-col gap-[18px]"}>
          <div className={"flex flex-wrap items-end justify-between gap-4"}>
            {header ?? saved?.header ?? (
              <div>
                <Link
                  to={"/reports"}
                  className={
                    "inline-flex items-center gap-1 text-[13px] font-bold text-[#6B4EFF] hover:text-[#5236E8]"
                  }>
                  <LuArrowLeft size={14} strokeWidth={2.4} />
                  All reports
                </Link>
                <h1
                  className={"mt-1.5 text-[26px] font-extrabold leading-tight"}>
                  {title}
                </h1>
                <p className={"mt-1.5 max-w-[760px] text-sm text-[#5B6472]"}>
                  {description}
                </p>
              </div>
            )}
            <div className={"flex flex-wrap items-center gap-2"}>
              {canAsk && !askOpen && (
                <button
                  type={"button"}
                  onClick={() => setAskOpen(true)}
                  className={
                    "inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#D9D1FF] bg-[#F5F3FF] px-4 text-[13px] font-bold text-[#6B4EFF] hover:bg-[#ECE7FF]"
                  }>
                  <LuSparkles size={15} />
                  Ask about this report
                </button>
              )}
              {actions}
              {saved?.actions}
              {onExport && (
                <button
                  type={"button"}
                  onClick={onExport}
                  className={
                    "inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#DCE2EA] bg-white px-4 text-[13px] font-bold text-[#1F2433] hover:border-[#6B4EFF]"
                  }>
                  <LuDownload size={16} strokeWidth={2.2} />
                  Export to Excel
                </button>
              )}
            </div>
          </div>
          {filters && (
            <div className={"flex flex-wrap items-center gap-2"}>{filters}</div>
          )}
          {saved?.note}
          {focus && (
            <div
              className={
                "flex flex-wrap items-center gap-2 rounded-xl border border-[#D9D1FF] bg-[#F5F3FF] px-3.5 py-2.5 text-[13px] font-semibold text-[#3F2EA8]"
              }>
              <LuSparkles size={14} />
              Showing only the{" "}
              {focus.keys.length === 1
                ? "row"
                : `${focus.keys.length} rows`}{" "}
              from the AI answer in “{focus.table}”.
              <button
                type={"button"}
                onClick={() => setFocus(null)}
                className={"ml-auto font-bold text-[#6B4EFF] hover:underline"}>
                Show all
              </button>
            </div>
          )}
          <ReportFocusContext.Provider value={focus}>
            {children}
          </ReportFocusContext.Provider>
          {saved?.dialogs}
        </div>
        {canAsk && askOpen && ask && (
          <ReportAskPanel
            data={ask}
            suggestions={askSuggestions}
            onClose={() => setAskOpen(false)}
            onFocus={(table, keys) => setFocus({ table, keys })}
          />
        )}
      </div>
    </div>
  );
};

export const KpiGrid: FC<{ children: ReactNode; min?: number }> = ({
  children,
  min = 200,
}) => (
  <div
    className={"grid gap-3"}
    style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}>
    {children}
  </div>
);

type Tone = "default" | "red" | "amber" | "green";
const TONES: Record<Tone, string> = {
  default: "text-[#1F2433]",
  red: "text-[#B42318]",
  amber: "text-[#8A4100]",
  green: "text-[#166534]",
};

export const Kpi: FC<{
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: Tone;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, note, tone = "default", active, onClick }) => {
  const body = (
    <>
      <div
        className={
          "text-xs font-bold uppercase tracking-[0.6px] text-[#5B6472]"
        }>
        {label}
      </div>
      <div
        className={classNames(
          "mt-1.5 text-[28px] font-extrabold leading-tight",
          TONES[tone],
        )}>
        {value}
      </div>
      {note && (
        <div className={"mt-0.5 text-[12.5px] text-[#5B6472]"}>{note}</div>
      )}
    </>
  );
  const box = classNames(
    "rounded-[14px] bg-white px-5 py-[18px] text-left",
    active
      ? "border-2 border-[#6B4EFF] bg-[#FBFAFF] px-[19px] py-[17px]"
      : "border border-[#EDEFF3]",
  );
  return onClick ? (
    <button
      type={"button"}
      onClick={onClick}
      className={classNames(box, "cursor-pointer")}>
      {body}
    </button>
  ) : (
    <div className={box}>{body}</div>
  );
};

export const Card: FC<{
  title?: string;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}> = ({ title, right, children, className }) => (
  <section
    className={classNames(
      "rounded-[14px] border border-[#EDEFF3] bg-white px-5 py-[18px]",
      className,
    )}>
    {(title || right) && (
      <div
        className={"mb-3 flex flex-wrap items-center justify-between gap-2.5"}>
        {title && <h2 className={"text-base font-extrabold"}>{title}</h2>}
        {right}
      </div>
    )}
    {children}
  </section>
);

export const Segmented = <T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) => (
  <div
    role={"group"}
    aria-label={label}
    className={
      "inline-flex flex-wrap gap-0.5 rounded-[10px] bg-[#F0F1F5] p-[3px]"
    }>
    {options.map((option) => (
      <button
        key={option.value}
        type={"button"}
        aria-pressed={option.value === value}
        onClick={() => onChange(option.value)}
        className={classNames(
          "h-8 rounded-lg px-3.5 text-[13px] font-bold",
          option.value === value
            ? "bg-white text-[#1F2433] shadow-[0_1px_3px_rgba(31,36,51,0.15)]"
            : "text-[#3F4757]",
        )}>
        {option.label}
      </button>
    ))}
  </div>
);

export const Avatar: FC<{ name: string; color?: string; square?: boolean }> = ({
  name,
  color,
  square,
}) => (
  <span
    aria-hidden={"true"}
    className={classNames(
      "mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center align-middle text-[11px] font-extrabold",
      square
        ? "rounded-lg bg-[#F0EDFF] text-[#4B32D6]"
        : "rounded-full text-white",
    )}
    style={square ? undefined : { backgroundColor: color || "#1E3A8A" }}>
    {getInitialsFromFullName(name) || "?"}
  </span>
);

const PILL: Record<StatusGroup, string> = {
  Ready: "bg-[#EEF0F4] text-[#3F4757]",
  "In Progress": "bg-[#E6F0FF] text-[#1D4ED8]",
  Waiting: "bg-[#FFF1E0] text-[#8A4100]",
  Scheduled: "bg-[#1F2433] text-white",
  Completed: "bg-[#E5F6EC] text-[#166534]",
};

export const StatusPill: FC<{ group: StatusGroup; label?: string | null }> = ({
  group,
  label,
}) => (
  <span
    className={classNames(
      "inline-block rounded-full px-2.5 py-[3px] text-xs font-bold",
      PILL[group],
    )}>
    {label || group}
  </span>
);

export const Legend: FC<{ items: { label: string; color: string }[] }> = ({
  items,
}) => (
  <div className={"flex flex-wrap gap-3.5"}>
    {items.map((item) => (
      <span
        key={item.label}
        className={
          "inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#3F4757]"
        }>
        <span
          className={"inline-block h-2.5 w-2.5 rounded-[3px]"}
          style={{ backgroundColor: item.color }}
        />
        {item.label}
      </span>
    ))}
  </div>
);

export const statusLegend = (groups: StatusGroup[]) =>
  groups.map((group) => ({ label: group, color: STATUS_COLORS[group] }));

/** One horizontal bar made of colored parts (widths in % of the track). */
export const StackedBar: FC<{
  parts: { value: number; color: string; label: string }[];
  max: number;
  height?: number;
}> = ({ parts, max, height = 14 }) => (
  <div
    className={"flex overflow-hidden rounded-full bg-[#EDEFF3]"}
    style={{ height }}
    role={"img"}
    aria-label={parts
      .map((p) => `${p.label} ${Math.round(p.value * 10) / 10}`)
      .join(", ")}>
    {parts.map((part) =>
      part.value > 0 ? (
        <div
          key={part.label}
          style={{
            width: `${max > 0 ? (part.value / max) * 100 : 0}%`,
            backgroundColor: part.color,
          }}
        />
      ) : null,
    )}
  </div>
);

/** Small progress bar for table cells. */
export const MiniBar: FC<{ pct: number; color: string; width?: number }> = ({
  pct,
  color,
  width = 120,
}) => (
  <span
    aria-hidden={"true"}
    className={
      "ml-2.5 inline-block h-2 overflow-hidden rounded-full bg-[#EDEFF3] align-middle"
    }
    style={{ width }}>
    <span
      className={"block h-full"}
      style={{
        width: `${Math.min(100, Math.max(0, pct))}%`,
        backgroundColor: color,
      }}
    />
  </span>
);

/** Vertical bars per month; `low` is drawn on top in a lighter color. */
export const ColumnChart: FC<{
  items: {
    key: string;
    label: string;
    value: number;
    low?: number;
    top?: string;
  }[];
  color?: string;
  lowColor?: string;
}> = ({ items, color = "#6B4EFF", lowColor = "#F2A39A" }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <Empty text={"Nothing to show for these dates."} />;
  return (
    <div>
      <div
        className={
          "flex h-[220px] items-end gap-2 border-b border-[#DCE2EA] pt-2.5 sm:gap-3.5"
        }>
        {items.map((item) => {
          const height = (item.value / max) * 170;
          const lowPct =
            item.value > 0 ? ((item.low || 0) / item.value) * 100 : 0;
          return (
            <div
              key={item.key}
              className={
                "flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5"
              }>
              <span className={"text-[11.5px] font-bold text-[#3F4757]"}>
                {item.top ?? item.value}
              </span>
              <div
                className={"w-full max-w-[54px] rounded-t-md"}
                style={{
                  height,
                  background: `linear-gradient(to bottom, ${lowColor} 0%, ${lowColor} ${lowPct}%, ${color} ${lowPct}%, ${color} 100%)`,
                }}
              />
            </div>
          );
        })}
      </div>
      <div className={"mt-2 flex gap-2 sm:gap-3.5"}>
        {items.map((item) => (
          <span
            key={item.key}
            className={
              "min-w-0 flex-1 text-center text-xs font-semibold text-[#5B6472]"
            }>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/** Report table; `pager` = the shared page selection under it (Roadmap 1867). */
export const TableBox: FC<{
  children: ReactNode;
  pager?: ComponentProps<typeof ListPager>;
}> = ({ children, pager }) => (
  <>
    <div className={"overflow-x-auto"}>
      <table className={"w-full border-collapse"}>{children}</table>
    </div>
    {pager && <ListPager className={"px-1"} {...pager} />}
  </>
);

export const Empty: FC<{ text: string }> = ({ text }) => (
  <div className={"py-10 text-center text-sm font-semibold text-[#5B6472]"}>
    {text}
  </div>
);

export const Loading: FC = () => (
  <div className={"py-16 text-center text-sm font-semibold text-[#5B6472]"}>
    Loading report…
  </div>
);

export const LoadError: FC = () => (
  <div
    className={
      "rounded-[14px] border border-[#FAD4D0] bg-[#FEF3F2] px-5 py-4 text-sm font-semibold text-[#B42318]"
    }>
    The report couldn&apos;t be loaded. Please refresh the page and try again.
  </div>
);

export const Note: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    className={
      "rounded-xl bg-[#F0EDFF] px-3.5 py-3 text-[13px] text-[#3F2EA8]"
    }>
    {children}
  </div>
);

export const NoAccess: FC<{ text: string }> = ({ text }) => (
  <div
    className={
      "flex flex-col items-center gap-3 rounded-[14px] border border-[#EDEFF3] bg-white px-6 py-14 text-center"
    }>
    <span
      className={
        "flex h-12 w-12 items-center justify-center rounded-full bg-[#FEECEC] text-[#B42318]"
      }>
      <LuLock size={20} />
    </span>
    <div className={"text-base font-extrabold"}>You don&apos;t have access</div>
    <p className={"max-w-[440px] text-sm text-[#5B6472]"}>{text}</p>
  </div>
);

/** Work name linking to the work item. */
export const WorkLink: FC<{ id: string; name: string }> = ({ id, name }) => (
  <Link to={`/work/${id}`} className={LINK}>
    {name || "Untitled work"}
  </Link>
);

/** First assignee (or the role when only a role is set) and "+N". */
export const AssigneeCell: FC<{
  people: { id: string; name: string; color?: string }[];
  role?: string | null;
}> = ({ people, role }) => {
  const first = people[0];
  if (!first || !first.id) {
    return (
      <span className={"text-[#5B6472]"}>
        {role ? `Role: ${role}` : "Not assigned"}
      </span>
    );
  }
  return (
    <span className={"inline-flex items-center"}>
      <Avatar name={first.name} color={first.color} />
      {first.name}
      {people.length > 1 && (
        <span className={"ml-1.5 text-xs font-bold text-[#5B6472]"}>
          +{people.length - 1}
        </span>
      )}
    </span>
  );
};
